import { useEffect, useState } from "react";
import { exchangeImpersonationCode } from "../services/impersonation/impersonation.service";

interface UseImpersonationBootResult {
    isExchanging: boolean;
    exchangeError: string | null;
}

/**
 * On boot, detect an `?ic=<code>` query param (the super-admin impersonation
 * handoff), derive the tenant slug from the current subdomain and persist it
 * to the localStorage "org" key the httpClient interceptor reads, exchange the
 * code for a tenant access token, persist the token in the same localStorage
 * shape AuthContext + httpClient consume, and reload so the normal auth flow
 * picks it up. No-op when the `ic` param is absent, so real Org Admin logins
 * are untouched.
 */
export const useImpersonationBoot = (): UseImpersonationBootResult => {
    const [isExchanging, setIsExchanging] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.has("ic");
    });
    const [exchangeError, setExchangeError] = useState<string | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("ic");

        if (!code) {
            return;
        }

        // The impersonation handoff always lands on the tenant's natural
        // subdomain (`{slug}.<domain>`), so the slug is just the first host
        // label — no need for a query param or /api/v1/custom-domains. Persist
        // it to the SAME localStorage key the httpClient request interceptor
        // reads ("org") so that after the reload below the interceptor builds
        // the tenant-subdomain baseURL directly
        // (`${ORG_PROTOCOL}://${org}.${ORG_BASEURL}`). Overwrite unconditionally
        // so the impersonated tenant always wins over any stale org slug.
        const orgSlug = window.location.hostname.split(".")[0];
        if (orgSlug) {
            localStorage.setItem("org", orgSlug);
        }

        // Scrub the ic param from the URL immediately so reloads or re-mounts
        // don't reuse a stale, single-use code.
        params.delete("ic");
        const newSearch = params.toString();
        const newUrl =
            window.location.pathname +
            (newSearch ? `?${newSearch}` : "") +
            window.location.hash;
        window.history.replaceState({}, "", newUrl);

        (async () => {
            try {
                const { data } = await exchangeImpersonationCode(code);
                const accessToken = data?.access_token;

                if (!accessToken) {
                    throw new Error("Exchange succeeded but no token in response.");
                }

                // Match the existing localStorage shape used by httpClient
                // (JSON.parse(localStorage.getItem("token"))?.access_token) and
                // AuthContext (dispatches SETTOKEN from JSON.parse of the same key).
                localStorage.setItem(
                    "token",
                    JSON.stringify({ access_token: accessToken })
                );

                // Mark THIS TAB as the impersonation tab. sessionStorage is
                // per-tab and survives the reload below. The backend sends the
                // X-Impersonation-Session header for any session of the
                // impersonated user/org — including the real user's own — so
                // the localStorage marker alone can't tell the impersonation
                // tab apart from a genuine tenant login. Only the tab that ran
                // this ?ic= handoff gets impersonation UI (banner, platform
                // lock bypass).
                sessionStorage.setItem("impersonation_boot", "1");

                // Reload so AuthContext + httpClient pick up the new token from
                // localStorage via the normal boot path.
                window.location.reload();
            } catch (error: any) {
                setExchangeError(
                    error?.response?.data?.errors?.messages?.[0] ||
                        error?.message ||
                        "Failed to exchange impersonation code."
                );
                setIsExchanging(false);
            }
        })();
    }, []);

    return { isExchanging, exchangeError };
};
